import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { User } from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthStoreService {
  private readonly API_BASE_URL = environment.apiBaseUrl;
  private readonly http: HttpClient;
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  private readonly tokenSubject = new BehaviorSubject<string | null>(null);
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private readonly profileLoadingSubject = new BehaviorSubject<boolean>(false);
  private refreshTokenRequest$?: Observable<string>;
  private hasCompletedInitialProfileLoad = false;
  private readonly isBrowser: boolean;

  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly token$ = this.tokenSubject.asObservable();
  readonly profileLoading$ = this.profileLoadingSubject.asObservable();
  readonly isAuthenticated$ = this.token$.pipe(
    map((token) => token !== null),
    distinctUntilChanged()
  );

  constructor(
    httpBackend: HttpBackend,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.http = new HttpClient(httpBackend);
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      const token = localStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('auth_refresh_token');
      if (token) {
        this.setAuthData(token, refreshToken ?? undefined);
      } else {
        this.clearStoredTokens();
      }
    }
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  get token(): string | null {
    return this.tokenSubject.value;
  }

  get refreshToken(): string | null {
    return this.refreshTokenSubject.value;
  }

  get hasCompletedInitialLoad(): boolean {
    return this.hasCompletedInitialProfileLoad;
  }

  get isBrowserPlatform(): boolean {
    return this.isBrowser;
  }

  setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  setProfileLoading(isLoading: boolean): void {
    this.profileLoadingSubject.next(isLoading);
  }

  markProfileLoaded(): void {
    this.hasCompletedInitialProfileLoad = true;
  }

  resetProfileLoaded(): void {
    this.hasCompletedInitialProfileLoad = false;
  }

  setAuthData(token: string, refreshToken?: string, options?: { resetProfile?: boolean }): void {
    if (!token) {
      return;
    }

    if (this.isBrowser) {
      localStorage.setItem('auth_token', token);
      if (refreshToken) {
        localStorage.setItem('auth_refresh_token', refreshToken);
      } else {
        localStorage.removeItem('auth_refresh_token');
      }
    }

    this.tokenSubject.next(token);
    this.refreshTokenSubject.next(refreshToken ?? null);

    if (options?.resetProfile ?? true) {
      this.resetProfileLoaded();
    }
  }

  clearAuthState(): void {
    this.clearStoredTokens();
    this.tokenSubject.next(null);
    this.refreshTokenSubject.next(null);
    this.currentUserSubject.next(null);
    this.resetProfileLoaded();
    this.profileLoadingSubject.next(false);
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    const refreshToken = this.refreshToken;
    if (!refreshToken) {
      this.clearAuthState();
      return throwError(() => new Error('No refresh token available'));
    }

    const refresh$ = this.http
      .post<{ token: string; refreshToken?: string }>(`${this.API_BASE_URL}/auth/refresh`, { refreshToken })
      .pipe(
        tap((response) => {
          this.setAuthData(response.token, response.refreshToken, { resetProfile: false });
        }),
        map((response) => response.token),
        catchError((error) => {
          this.clearAuthState();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshTokenRequest$ = undefined;
        })
      );

    this.refreshTokenRequest$ = refresh$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    return this.refreshTokenRequest$;
  }

  getValidAccessToken(): Observable<string | null> {
    const token = this.token;
    if (!token) {
      return of(null);
    }

    if (!this.isTokenExpired(token)) {
      return of(token);
    }

    return this.refreshAccessToken().pipe(map((refreshedToken) => refreshedToken ?? null));
  }

  private clearStoredTokens(): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
  }

  private isTokenExpired(token: string, offsetSeconds: number = 30): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const payload = this.decodeJwtPayload(token);
    if (!payload?.exp) {
      return true;
    }

    const expiryTimeMs = payload.exp * 1000;
    const bufferMs = offsetSeconds * 1000;
    return expiryTimeMs - bufferMs <= Date.now();
  }

  private decodeJwtPayload(token: string): { exp?: number } | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const segments = token.split('.');
      if (segments.length < 2) {
        return null;
      }

      const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const decoded = typeof atob === 'function' ? atob(padded) : '';

      if (!decoded) {
        return null;
      }

      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }
}
